import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import type {
  NotifySound,
  Project,
  ReplyPermission,
  ReplyTone,
} from '@/lib/types'
import { cn } from '@/lib/utils'
import { playNotifySound } from '@/lib/sound'
import { ToneSlider } from './ToneSlider'
import { useNotify } from './ConfirmDialog'
import {
  X,
  Loader2,
  Sparkles,
  ShieldCheck,
  Check,
  Lock,
  Volume2,
  Play,
} from 'lucide-react'

interface Props {
  /** The project to configure. Null = closed. */
  project: Project | null
  /** Only the owner can edit; others see the controls disabled. */
  isOwner: boolean
  onClose: () => void
  /** Persist the new settings. Resolves when saved. */
  onSave: (
    projectId: string,
    patch: {
      reply_tone: ReplyTone
      reply_permission: ReplyPermission
      reply_suggestions_enabled: boolean
      notify_sound: NotifySound
    },
  ) => Promise<void>
}

const SOUNDS: NotifySound[] = ['off', '7', '8', '9']

const PERMS: ReplyPermission[] = ['owner', 'subscribers', 'all_members']

type SettingsTab = 'perm' | 'tone' | 'notif'
const TABS: SettingsTab[] = ['perm', 'tone', 'notif']
const TAB_LABEL: Record<SettingsTab, string> = {
  perm: 'projectSettings.tabPerm',
  tone: 'projectSettings.tabTone',
  notif: 'projectSettings.tabNotif',
}

/** Per-channel project settings — AI suggestion register + who is allowed
 *  to reply to the WhatsApp contact. Opened from the project's contextual
 *  (kebab) menu in the sidebar. Owner-only editing. */
export function ProjectSettingsModal({
  project,
  isOwner,
  onClose,
  onSave,
}: Props) {
  const { t } = useTranslation()
  const notify = useNotify()
  const [tone, setTone] = useState<ReplyTone>('vous_pro')
  const [perm, setPerm] = useState<ReplyPermission>('owner')
  const [suggestionsOn, setSuggestionsOn] = useState(true)
  const [sound, setSound] = useState<NotifySound>('off')
  const [saving, setSaving] = useState(false)
  const [tab, setTab] = useState<SettingsTab>('perm')

  // Sync local state whenever a (different) project opens.
  useEffect(() => {
    if (!project) return
    setTone(project.reply_tone ?? 'vous_pro')
    setPerm(project.reply_permission ?? 'owner')
    setSuggestionsOn(project.reply_suggestions_enabled !== false)
    setSound(project.notify_sound ?? 'off')
    setTab('perm')
  }, [project])

  if (!project) return null

  const dirty =
    tone !== (project.reply_tone ?? 'vous_pro') ||
    perm !== (project.reply_permission ?? 'owner') ||
    suggestionsOn !== (project.reply_suggestions_enabled !== false) ||
    sound !== (project.notify_sound ?? 'off')

  async function save() {
    if (!project || !isOwner || !dirty || saving) return
    setSaving(true)
    try {
      await onSave(project.id, {
        reply_tone: tone,
        reply_permission: perm,
        reply_suggestions_enabled: suggestionsOn,
        notify_sound: sound,
      })
      notify({ title: t('projectSettings.savedToast'), tone: 'success' })
      onClose()
    } catch (e) {
      notify({
        title: t('projectSettings.saveError'),
        message: e instanceof Error ? e.message : String(e),
        tone: 'error',
      })
    } finally {
      setSaving(false)
    }
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/30 px-4 py-6 backdrop-blur-sm"
      onClick={onClose}
    >
      <div
        className="flex max-h-[88vh] w-full max-w-lg flex-col overflow-hidden rounded-3xl border border-[var(--color-border-soft)] bg-[var(--color-surface)] shadow-[var(--shadow-modal)]"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex shrink-0 items-start justify-between gap-3 border-b border-[var(--color-border-soft)] px-6 py-4">
          <div className="min-w-0">
            <div className="text-[11px] font-semibold uppercase tracking-wider text-[var(--color-text-dim)]">
              {t('projectSettings.title')}
            </div>
            <div className="mt-0.5 truncate text-[15px] font-semibold text-[var(--color-text-strong)]">
              {project.name}
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="rounded-xl p-1.5 text-[var(--color-text-dim)] transition-colors hover:bg-[var(--color-surface-2)] hover:text-[var(--color-text)]"
            aria-label={t('common.close')}
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Tab bar */}
        <div className="flex shrink-0 gap-1 border-b border-[var(--color-border-soft)] px-3">
          {TABS.map((value) => {
            const active = tab === value
            return (
              <button
                key={value}
                type="button"
                onClick={() => setTab(value)}
                className={cn(
                  'relative px-3 py-2.5 text-[12.5px] font-medium transition-colors',
                  active
                    ? 'text-[var(--color-text-strong)]'
                    : 'text-[var(--color-text-dim)] hover:text-[var(--color-text)]',
                )}
              >
                {t(TAB_LABEL[value])}
                {active && (
                  <span className="absolute inset-x-2 -bottom-px h-0.5 rounded-full bg-[var(--color-accent)]" />
                )}
              </button>
            )
          })}
        </div>

        {/* Body — min-height keeps the modal a stable height across tabs
            (sized to the tallest tab, « Ton & suggestions »). */}
        <div className="flex min-h-[380px] flex-1 flex-col gap-6 overflow-y-auto p-6">
          {!isOwner && (
            <div className="flex items-center gap-2 rounded-xl border border-[var(--color-border)] bg-[var(--color-surface-2)] px-3 py-2.5 text-[12px] text-[var(--color-text-dim)]">
              <Lock className="h-3.5 w-3.5 shrink-0" />
              {t('projectSettings.ownerOnly')}
            </div>
          )}

          {/* ── Onglet : Permissions ── */}
          {tab === 'perm' && (
            <section>
              <div className="mb-1 flex items-center gap-1.5 text-[12.5px] font-semibold text-[var(--color-text-strong)]">
                <ShieldCheck className="h-4 w-4 text-[var(--color-text-dim)]" />
                {t('projectSettings.permTitle')}
              </div>
              <div className="mb-2.5 text-[11.5px] text-[var(--color-text-dim)]">
                {t('projectSettings.permHint')}
              </div>
              <div className="space-y-1.5">
                {PERMS.map((value) => (
                  <OptionRow
                    key={value}
                    selected={perm === value}
                    disabled={!isOwner}
                    onClick={() => setPerm(value)}
                    label={t(`projectSettings.perm.${value}`)}
                    hint={t(`projectSettings.perm.${value}Hint`)}
                  />
                ))}
              </div>
            </section>
          )}

          {/* ── Onglet : Ton & suggestions ── */}
          {tab === 'tone' && (
            <>
              {/* AI suggestions on/off */}
              <section>
                <button
                  type="button"
                  onClick={() => isOwner && setSuggestionsOn((v) => !v)}
                  disabled={!isOwner}
                  className={cn(
                    'flex w-full items-center gap-3 rounded-xl border px-3 py-2.5 text-left transition-colors',
                    'border-[var(--color-border-soft)] bg-[var(--color-surface)]',
                    isOwner && 'hover:border-[var(--color-border)]',
                    !isOwner && 'cursor-not-allowed opacity-60',
                  )}
                >
                  <Sparkles className="h-4 w-4 shrink-0 text-[var(--color-text-dim)]" />
                  <span className="min-w-0 flex-1">
                    <span className="block text-[12.5px] font-medium text-[var(--color-text-strong)]">
                      {t('projectSettings.suggestionsTitle')}
                    </span>
                    <span className="block text-[11px] text-[var(--color-text-dim)]">
                      {t('projectSettings.suggestionsHint')}
                    </span>
                  </span>
                  <span
                    className={cn(
                      'relative h-5 w-9 shrink-0 rounded-full transition-colors',
                      suggestionsOn
                        ? 'bg-[var(--color-accent)]'
                        : 'bg-[var(--color-border)]',
                    )}
                  >
                    <span
                      className={cn(
                        'absolute top-0.5 h-4 w-4 rounded-full bg-white shadow-sm transition-all',
                        suggestionsOn ? 'left-[18px]' : 'left-0.5',
                      )}
                    />
                  </span>
                </button>
              </section>

              {/* Tone — only relevant when suggestions are enabled. */}
              <section className={cn(!suggestionsOn && 'opacity-40')}>
                <div className="mb-1 flex items-center gap-1.5 text-[12.5px] font-semibold text-[var(--color-text-strong)]">
                  <Sparkles className="h-4 w-4 text-[var(--color-text-dim)]" />
                  {t('projectSettings.toneTitle')}
                </div>
                <div className="mb-3 text-[11.5px] text-[var(--color-text-dim)]">
                  {t('projectSettings.toneHint')}
                </div>
                <ToneSlider
                  value={tone}
                  disabled={!isOwner || !suggestionsOn}
                  onChange={setTone}
                  t={t}
                />
              </section>
            </>
          )}

          {/* ── Onglet : Notifications ── */}
          {tab === 'notif' && (
            <section>
              <div className="mb-1 flex items-center gap-1.5 text-[12.5px] font-semibold text-[var(--color-text-strong)]">
                <Volume2 className="h-4 w-4 text-[var(--color-text-dim)]" />
                {t('projectSettings.soundTitle')}
              </div>
              <div className="mb-2.5 text-[11.5px] text-[var(--color-text-dim)]">
                {t('projectSettings.soundHint')}
              </div>
              <div className="space-y-1.5">
                {SOUNDS.map((value) => (
                  <div key={value} className="flex items-center gap-1.5">
                    <div className="min-w-0 flex-1">
                      <OptionRow
                        selected={sound === value}
                        disabled={!isOwner}
                        onClick={() => {
                          setSound(value)
                          if (value !== 'off') playNotifySound(value)
                        }}
                        label={t(`projectSettings.sound.${value}`)}
                        hint={t(`projectSettings.sound.${value}Hint`)}
                      />
                    </div>
                    {value !== 'off' && (
                      <button
                        type="button"
                        onClick={() => playNotifySound(value)}
                        title={t('projectSettings.soundPreview')}
                        aria-label={t('projectSettings.soundPreview')}
                        className="shrink-0 rounded-lg border border-[var(--color-border-soft)] p-2 text-[var(--color-text-dim)] transition-colors hover:border-[var(--color-link)] hover:text-[var(--color-link)]"
                      >
                        <Play className="h-3.5 w-3.5" />
                      </button>
                    )}
                  </div>
                ))}
              </div>
            </section>
          )}
        </div>

        {/* Footer */}
        <div className="flex shrink-0 items-center justify-end gap-2 border-t border-[var(--color-border-soft)] bg-[var(--color-surface-2)] px-6 py-3">
          <button
            type="button"
            onClick={onClose}
            className="rounded-xl px-3 py-1.5 text-[12px] font-medium text-[var(--color-text-dim)] transition-colors hover:bg-[var(--color-surface-3)] hover:text-[var(--color-text)]"
          >
            {t('common.cancel')}
          </button>
          <button
            type="button"
            onClick={save}
            disabled={!isOwner || !dirty || saving}
            className="inline-flex items-center gap-1.5 rounded-xl bg-[var(--color-accent)] px-3.5 py-1.5 text-[12px] font-semibold text-white transition-colors hover:bg-[var(--color-accent-hover)] disabled:cursor-not-allowed disabled:opacity-50"
          >
            {saving ? (
              <Loader2 className="h-3.5 w-3.5 animate-spin" />
            ) : (
              <Check className="h-3.5 w-3.5" />
            )}
            {saving ? t('projectSettings.saving') : t('projectSettings.save')}
          </button>
        </div>
      </div>
    </div>
  )
}

function OptionRow({
  selected,
  disabled,
  onClick,
  label,
  hint,
}: {
  selected: boolean
  disabled: boolean
  onClick: () => void
  label: string
  hint: string
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className={cn(
        'flex w-full items-start gap-2.5 rounded-xl border px-3 py-2.5 text-left transition-colors',
        selected
          ? 'border-[var(--color-accent)] bg-[var(--color-accent-bg)]'
          : 'border-[var(--color-border-soft)] bg-[var(--color-surface)] hover:border-[var(--color-border)]',
        disabled && 'cursor-not-allowed opacity-60',
      )}
    >
      <span
        className={cn(
          'mt-0.5 flex h-4 w-4 shrink-0 items-center justify-center rounded-full border',
          selected
            ? 'border-[var(--color-accent)] bg-[var(--color-accent)]'
            : 'border-[var(--color-border)]',
        )}
      >
        {selected && <span className="h-1.5 w-1.5 rounded-full bg-white" />}
      </span>
      <span className="min-w-0">
        <span className="block text-[12.5px] font-medium text-[var(--color-text)]">
          {label}
        </span>
        <span className="block text-[11px] text-[var(--color-text-dim)]">
          {hint}
        </span>
      </span>
    </button>
  )
}
